import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography, Rating, createTheme, ThemeProvider } from "@mui/material";
import axios from "axios";
import { useParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

// Create a dark theme using Material-UI's theme creator
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#1E90FF", // Light blue primary color
    },
    background: {
      default: "#000000", // Dark background color
      paper: "#000000", // Slightly lighter background for paper elements
    },
    text: {
      primary: "#ffffff", // Light text color
      secondary: "#b0b0b0", // Lighter text color for secondary text
    },
    error: {
      main: "#f44336", // Red for error messages
    },
  },
});

const FeedbackFormMyPosts = ({ userId, postId }) => {
  // State to handle form data
  const [feedback, setFeedback] = useState({
    rating: 0,
    comments: "",
  });

  const [existingFeedback, setExistingFeedback] = useState(null); // Store existing feedback
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false); // State to track if the user is updating feedback

  // Check if feedback already exists on component mount
  useEffect(() => {
    const checkFeedback = async () => {
      try {
        const response = await axios.post(`${API_URL}/check_feedback_my_posts/`, {
          post_id: postId,
          user_id: userId,
        });

        if (response.status === 200) {
          if (response.data.message === "Feedback exists for this post.") {
            setExistingFeedback(response.data); // Set existing feedback data
            setFeedback({
              rating: response.data.rating,
              comments: response.data.comments,
            });
          } else {
            setExistingFeedback(null); // Reset if no feedback is found
          }
        }
      } catch (err) {
        console.error("Error checking feedback:", err);
      }
    };

    checkFeedback();
  }, [postId, userId]);

  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback({
      ...feedback,
      [name]: value,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (feedback.rating === 0) {
      setError("Please rate the session.");
      return;
    }

    if (feedback.comments.trim() === "") {
      setError("Please provide a comment.");
      return;
    }

    // Prepare data for API call
    const data = {
      post_id: postId, // Pass the postId prop
      student_id: userId, // Pass the userId prop
      student_rating: feedback.rating,
      student_comment: feedback.comments,
    };

    try {
      // Make the API call to either update or create feedback
      const response = await axios.post(`${API_URL}/create_user_feedback_my_posts/`, data);

      if (response.status === 200) {
        setSubmitted(true); // Set submitted to true on successful submission
        setIsUpdating(false); // Reset the update state after successful submission
      } else {
        setError("Something went wrong, please try again later.");
      }
    }
    catch (err) {
      setError("Failed to submit feedback. Please try again.");
      console.error(err);
    }
  };

  // Disable update feedback button if feedback already exists
  const disableUpdateButton = existingFeedback !== null;

  return (
    <div>
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            maxWidth: 500,
            margin: "0 auto",
            padding: "20px",
            marginTop: "100px",
            borderRadius: "8px",
            boxShadow: 3,
            backgroundColor: "background.paper", // Dark background color for the box
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom color="text.primary" sx={{ letterSpacing: 4 }}>
            Session Feedback
          </Typography>

          {submitted ? (
            <Typography variant="h6" color="primary" sx={{ letterSpacing: 3 }}>
              Thank you for your feedback!
            </Typography>
          ) : existingFeedback ? (
            <Typography variant="h6" color="primary" sx={{ letterSpacing: 3 }}>
              Existing Feedback Found. You can update it.
            </Typography>
          ) : (
            <Typography variant="h6" color="primary" sx={{ letterSpacing: 3 }}>
              No feedback found. Please provide your feedback.
            </Typography>
          )}

          <form onSubmit={handleSubmit}>
            <Typography variant="body1" sx={{ marginBottom: "0.5rem", letterSpacing: 3 }} color="text.primary">
              How would you rate the session?
            </Typography>
            <Rating
              name="rating"
              value={feedback.rating}
              onChange={(_, newValue) => handleChange({ target: { name: "rating", value: newValue } })}
              precision={0.5}
              sx={{ marginBottom: "1rem", letterSpacing: 3 }}
            />
            <TextField
              label="Comments"
              name="comments"
              value={feedback.comments}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              inputProps={{ maxLength: 100 }}
              helperText={`${feedback.comments.length}/100 characters`}
              sx={{ marginBottom: "1rem", letterSpacing: 3 }}
              InputLabelProps={{
                style: { color: "white", letterSpacing: 3 },
              }}
              InputProps={{
                style: { color: "white", letterSpacing: 3 },
              }}
            />


            {error && (
              <Typography variant="body2" color="error" sx={{ marginBottom: "1rem", letterSpacing: 3 }}>
                {error}
              </Typography>
            )}

            {/* Disable the button if feedback already exists */}
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              disabled={disableUpdateButton}
              sx={{ letterSpacing: 4 }}
            >
              {existingFeedback ? "Update Feedback" : "Submit Feedback"}
            </Button>
          </form>
        </Box>
      </ThemeProvider>
    </div>
  );
};

export default FeedbackFormMyPosts;
